import { CROP_PHASE_LABELS, MONTH_NAMES_PT } from '../constants/maintenance.constants';
import type { ParcellingTableRow } from '../components/Tables/ParcellingTable/ParcellingTable';
import type {
	MaintenanceCyclePhasesResponse,
	MaintenanceDemandByPeriod,
	ParcellingBalanceUnit,
} from '../types';
import { getCyclePhasesByMonth, mapDynamicMonthlyParcels } from './dynamicParcellingCycle';

type NutrientParcel = {
	month?: number;
	weeks?: number;
	phase?: string;
	N?: number;
	P?: number;
	K?: number;
	Ca?: number;
	Mg?: number;
	S?: number;
	B?: number;
	Cu?: number;
	Fe?: number;
	Mn?: number;
	Zn?: number;
};

export type ParcellingTableSource = {
	typeParcelling?: 'fixed' | 'dynamic';
	fixedParcel?: NutrientParcel[];
	dynamicParcel?: NutrientParcel[];
};

export type ParcellingDemandSource = {
	periods?: string[];
	demandByPeriod?: MaintenanceDemandByPeriod;
	demandAnnual?: Record<string, number>;
};

const DEMAND_COLUMN_KEYS = {
	n: 'N',
	p: 'P2O5',
	k: 'K2O',
	ca: 'Ca',
	mg: 'Mg',
	s: 'S',
	b: 'B',
	cu: 'Cu',
	fe: 'Fe',
	mn: 'Mn',
	zn: 'Zn',
} as const;

const demandFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

const phaseLabel = (phase?: string) => CROP_PHASE_LABELS[phase ?? ''] ?? phase ?? '—';

const phasesLabel = (phases: string[]) => (phases.length ? phases.map(phaseLabel).join('/') : '—');

const monthLabel = (month?: number) => {
	const key = String(month ?? '').padStart(2, '0');
	return MONTH_NAMES_PT[key] ?? key;
};

const toRow = (
	month: string,
	parcel: NutrientParcel,
	phase = phaseLabel(parcel.phase),
	format: (value: number) => number | string = Number
): ParcellingTableRow => ({
	month,
	phase,
	n: format(Number(parcel.N ?? 0)),
	p: format(Number(parcel.P ?? 0)),
	k: format(Number(parcel.K ?? 0)),
	ca: format(Number(parcel.Ca ?? 0)),
	mg: format(Number(parcel.Mg ?? 0)),
	s: format(Number(parcel.S ?? 0)),
	b: format(Number(parcel.B ?? 0)),
	cu: format(Number(parcel.Cu ?? 0)),
	fe: format(Number(parcel.Fe ?? 0)),
	mn: format(Number(parcel.Mn ?? 0)),
	zn: format(Number(parcel.Zn ?? 0)),
});

const monthKeyLabel = (key: string) => monthLabel(Number(key.split('-')[1]));

const dynamicCycleOf = (
	parcelling: ParcellingTableSource | null | undefined,
	cycle?: MaintenanceCyclePhasesResponse | null
) => (parcelling?.typeParcelling === 'dynamic' && cycle?.phases.length ? cycle : null);

export const mapParcellingToTableRows = (
	parcelling?: ParcellingTableSource | null,
	cycle?: MaintenanceCyclePhasesResponse | null
): ParcellingTableRow[] => {
	if (!parcelling) return [];

	const dynamicCycle = dynamicCycleOf(parcelling, cycle);
	if (dynamicCycle) {
		return mapDynamicMonthlyParcels(dynamicCycle).map((month) =>
			toRow(
				monthKeyLabel(month.key),
				month.parcel,
				phasesLabel(month.phases),
				demandFormatter.format
			)
		);
	}

	if (parcelling.typeParcelling === 'dynamic') {
		return (parcelling.dynamicParcel ?? []).map((parcel, index) =>
			toRow(`${index + 1}ª semana`, parcel)
		);
	}

	return (parcelling.fixedParcel ?? []).map((parcel, index) =>
		toRow(parcel.month ? monthLabel(parcel.month) : monthLabel(index + 1), parcel)
	);
};

const isWeeklyPeriod = (period: string) => period.split(/[/-]/).length === 3;

const periodLabel = (period: string, index: number) => {
	if (isWeeklyPeriod(period)) return `${index + 1}ª semana`;
	return monthLabel(Number(period.replace(/\//g, '-').split('-')[1]));
};

const periodPhases = (parcelling: ParcellingTableSource | null | undefined, periods: string[]) => {
	if (!parcelling || !periods.length) return [];
	if (parcelling.typeParcelling !== 'dynamic') {
		return (parcelling.fixedParcel ?? []).map((parcel) => parcel.phase);
	}
	if (!isWeeklyPeriod(periods[0])) return [];
	return (parcelling.dynamicParcel ?? []).flatMap((parcel) =>
		Array.from({ length: Number(parcel.weeks) || 0 }, () => parcel.phase)
	);
};

const formatDemand = (value: number | undefined, factor: number | null) => {
	if (value == null || !Number.isFinite(value) || factor == null) return '—';
	return demandFormatter.format(value * factor);
};

const toMonthlyDemand = (periods: string[], demandByPeriod: MaintenanceDemandByPeriod) => {
	const periodKeys = periods.map((period) => period.replace(/\//g, '-').slice(0, 7));
	const keys = [...new Set(periodKeys)];
	const monthlyDemand = Object.fromEntries(
		Object.values(DEMAND_COLUMN_KEYS).map((nutrient) => {
			const totals = keys.map(() => 0);
			periodKeys.forEach((key, index) => {
				totals[keys.indexOf(key)] += Number(demandByPeriod[nutrient]?.[index] ?? 0);
			});
			return [nutrient, totals];
		})
	) as MaintenanceDemandByPeriod;
	return { keys, monthlyDemand };
};

const demandCells = (valueOf: (nutrient: string) => number | undefined, factor: number | null) =>
	Object.fromEntries(
		Object.entries(DEMAND_COLUMN_KEYS).map(([column, nutrient]) => [
			column,
			formatDemand(valueOf(nutrient), factor),
		])
	) as Record<keyof typeof DEMAND_COLUMN_KEYS, string>;

const periodCells = (
	demandByPeriod: MaintenanceDemandByPeriod,
	index: number,
	factor: number | null
) => demandCells((nutrient) => demandByPeriod[nutrient]?.[index], factor);

const periodDemandRows = (
	periods: string[],
	demandByPeriod: MaintenanceDemandByPeriod,
	parcelling: ParcellingTableSource | null | undefined,
	factor: number | null,
	cycle?: MaintenanceCyclePhasesResponse | null
): ParcellingTableRow[] => {
	const dynamicCycle = dynamicCycleOf(parcelling, cycle);
	if (dynamicCycle) {
		const phasesByMonth = getCyclePhasesByMonth(dynamicCycle.phases);
		const { keys, monthlyDemand } = toMonthlyDemand(periods, demandByPeriod);
		return keys.map((key, index) => ({
			month: monthKeyLabel(key),
			phase: phasesLabel(phasesByMonth.get(key) ?? []),
			...periodCells(monthlyDemand, index, factor),
		}));
	}

	const phases = periodPhases(parcelling, periods);

	return periods.map((period, index) => ({
		month: periodLabel(period, index),
		phase: phases[index] ? phaseLabel(phases[index]) : '—',
		...periodCells(demandByPeriod, index, factor),
	}));
};

export const mapDemandToTableRows = (
	demand: ParcellingDemandSource | null | undefined,
	parcelling: ParcellingTableSource | null | undefined,
	unit: Exclude<ParcellingBalanceUnit, 'percentage'>,
	plantsByHa: number | null,
	cycle?: MaintenanceCyclePhasesResponse | null
): ParcellingTableRow[] => {
	const periods = demand?.periods ?? [];
	const demandByPeriod = demand?.demandByPeriod;
	if (!periods.length || !demandByPeriod) return [];

	const gramsPerPlantFactor = plantsByHa ? 1000 / plantsByHa : null;
	const factor = unit === 'kgPerHa' ? 1 : gramsPerPlantFactor;

	const rows = periodDemandRows(periods, demandByPeriod, parcelling, factor, cycle);
	const demandAnnual = demand?.demandAnnual;
	if (!demandAnnual) return rows;

	return [
		...rows,
		{
			month: 'Demanda anual',
			phase: '—',
			isTotal: true,
			...demandCells((nutrient) => demandAnnual[nutrient], factor),
		},
	];
};

export const calculatePlantsByHa = (sectors: Array<{ size?: number; amountOfPlants?: number }>) => {
	const totalSize = sectors.reduce((sum, sector) => sum + (Number(sector.size) || 0), 0);
	const totalPlants = sectors.reduce(
		(sum, sector) => sum + (Number(sector.amountOfPlants) || 0),
		0
	);
	return totalSize > 0 && totalPlants > 0 ? totalPlants / totalSize : null;
};
